/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/optionHelpers.ts (Dynamic Destinations)
 🗓️ Created: 2025-01-27
 🧩 Description:
    Helper functions for Dynamic Destination options management.
────────────────────────────────────────────────────────────────*/

import { DynamicDestinationOptionFormData, DynamicDestinationOption, SourceType } from '../types';
import { createEmptyOption } from './formDefaults';

/**
 * Update option priorities
 */
export function updateOptionPriorities(
  options: DynamicDestinationOptionFormData[]
): DynamicDestinationOptionFormData[] {
  return options
    .map((option) => ({ ...option }))
    .sort((a, b) => a.priority - b.priority)
    .map((option, index) => ({ ...option, priority: index + 1 }));
}

/**
 * Map API option to form option
 */
export function mapApiOptionToForm(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  option: any,
  index: number
): DynamicDestinationOptionFormData {
  return {
    id: option.id ? Number(option.id) : undefined,
    matchValue: option.matchValue ?? option.match_value ?? '',
    destinationModule: option.destinationModule ?? option.destination_module ?? '',
    destinationValue: option.destination ?? option.destination_value ?? '',
    enabled: Boolean(option.enabled ?? option.isEnabled ?? true),
    priority:
      Number(option.priority ?? option.sort_order ?? option.order ?? index + 1) || index + 1,
  };
}

/**
 * Map form option to API payload
 */
export function mapFormOptionToApi(
  option: DynamicDestinationOptionFormData
): {
  matchValue: string;
  destinationModule: string;
  destination: string;
  enabled: boolean;
  priority: number;
} {
  return {
    matchValue: option.matchValue.trim(),
    destinationModule: option.destinationModule.trim(),
    destination: option.destinationValue.trim(),
    enabled: option.enabled,
    priority: option.priority,
  };
}

/**
 * Sanitize form data
 */
export function sanitizeFormData(formData: {
  name: string;
  sourceType: SourceType | string;
  sourceConfig: string;
  queryTemplate: string;
  matchField: string;
  defaultModule: string;
  defaultDestination: string;
  description: string;
  active: boolean;
  options: DynamicDestinationOptionFormData[];
}): {
  name: string;
  sourceType: SourceType;
  sourceConfig: string;
  queryTemplate: string;
  matchField: string;
  defaultModule: string;
  defaultDestination: string;
  description: string;
  active: boolean;
  options: DynamicDestinationOptionFormData[];
} {
  return {
    ...formData,
    name: formData.name.trim(),
    sourceType: (formData.sourceType === 'DB' || formData.sourceType === 'URL') ? formData.sourceType : 'DB' as SourceType,
    sourceConfig: formData.sourceConfig.trim(),
    queryTemplate: formData.queryTemplate.trim(),
    matchField: formData.matchField.trim(),
    defaultModule: formData.defaultModule.trim(),
    defaultDestination: formData.defaultDestination.trim(),
    description: formData.description.trim(),
    options: formData.options
      .map((option) => ({
        ...option,
        matchValue: option.matchValue.trim(),
        destinationModule: option.destinationModule.trim(),
        destinationValue: option.destinationValue.trim(),
        enabled: Boolean(option.enabled),
        priority: Number(option.priority) || 1,
      }))
      .sort((a, b) => a.priority - b.priority)
      .map((option, index) => ({ ...option, priority: index + 1 })),
  };
}

